import { staticError, useGuardedEffect } from "@p0security/frontend-core";
import { dfs } from "@p0security/shared/graph/graph";
import { isNode } from "@p0security/shared/graph/types";
import {
  AssessmentNodes,
  GrantNode,
} from "@p0security/shared/types/assessment/data/index";
import { Identity } from "@p0security/shared/types/assessment/data/index";
import { List } from "@p0security/ui";
import { TIME_TO_DEBOUNCE, sleep } from "@p0security/util/sleep";
import { ConditionalVirtualList } from "components/common/ConditionalVirtualList";
import { sortBy } from "lodash";
import { useCallback, useState } from "react";

import { IdentityCell, IdentityLink } from "../../cells/Identity";

type AssessmentIdentity = AssessmentNodes["identity"] & {
  id: string;
};

export const GrantAccessors: React.FC<{ node: GrantNode }> = ({ node }) => {
  const [result, setResult] = useState<Identity[] | undefined>(undefined);

  const renderAccessorItem = useCallback(
    (accessor: Identity, ix: number) => (
      <List.Item key={ix}>
        <IdentityLink data={accessor} type="short" show="grant" />{" "}
        <IdentityCell identity={accessor} />
      </List.Item>
    ),
    []
  );

  useGuardedEffect(
    (cancellation) => async () => {
      setResult(undefined);
      await sleep(TIME_TO_DEBOUNCE);
      if (cancellation.isCancelled) return;

      const before = performance.now();

      // Use DFS to traverse up the parent hierarchy to find all upstream identities
      const traversed = await dfs<
        AssessmentNodes,
        Record<string, AssessmentIdentity>
      >({ nodes: [node] }, "parents", {
        init: (node) => {
          if (isNode("identity")(node)) {
            return {
              [node.key]: { ...node.data, id: node.key } as AssessmentIdentity,
            };
          }
          return {};
        },
        combine: (left, right) => {
          // Mutate in-place to make combination O(N) instead of O(N^2)
          for (const [k, v] of Object.entries(right)) {
            left[k] = v;
          }
          return left;
        },
      });

      // Extract all unique identities from the traversal result
      const accessors = Object.values(traversed[`grant:${node.key}`] || {});

      const sortedAccessors = sortBy(accessors, ["principalType", "id"]);

      // eslint-disable-next-line no-console
      console.log(
        "Time to gather grant accessors",
        (performance.now() - before).toFixed(1),
        "ms"
      );

      setResult(sortedAccessors);
    },
    [node],
    staticError
  );

  if (result === undefined) {
    return <div data-testid="grant-accessors-loading">Loading...</div>;
  }

  if (result.length === 0) {
    return <div data-testid="grant-accessors-empty">(No upstream access)</div>;
  }

  return (
    <div data-testid="grant-accessors-loaded">
      <ConditionalVirtualList
        data={result}
        renderItem={renderAccessorItem}
        height={280}
        itemHeight={40}
        virtualThreshold={20}
      />
    </div>
  );
};
